import { useState } from "react";

import { ImageDropZone } from "@/components/kit";
import { useUploadBannerImage } from "@/hooks/api/banner/useUploadBannerImage";
import { getApiErrorMessage } from "@/utils/api-error";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const getRejectionMessage = (file: File): string | null => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type))
    return "Only JPEG, PNG or WebP images are allowed";
  if (file.size > MAX_IMAGE_SIZE_BYTES) return "Image must be 10 MB or smaller";

  return null;
};

interface BannerImageDropzoneProps {
  value: string;
  errorMessage?: string;
  onChange: (url: string) => void;
}

export const BannerImageDropzone = ({
  value,
  errorMessage,
  onChange,
}: BannerImageDropzoneProps) => {
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  const {
    mutate: uploadImage,
    isPending: isUploading,
    error: uploadError,
  } = useUploadBannerImage();

  const onFile = (file: File) => {
    const rejection = getRejectionMessage(file);

    if (rejection) {
      setRejectionMessage(rejection);

      return;
    }

    setRejectionMessage(null);
    uploadImage(file, { onSuccess: ({ url }) => onChange(url) });
  };

  const message =
    rejectionMessage ??
    (uploadError
      ? getApiErrorMessage(uploadError, "Upload failed, please try again")
      : errorMessage);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">
        Banner image <span className="text-(--danger)">*</span>
      </span>
      <ImageDropZone
        aspectClassName="aspect-[2/1]"
        errorMessage={message}
        hint="JPEG, PNG or WebP up to 10 MB. Wide images around 2:1 fit best."
        isUploading={isUploading}
        label="Banner image"
        value={value}
        onFile={onFile}
      />
    </div>
  );
};
